import { Box, Button, CircularProgress, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

const isChunkLoadError = (error) =>
  error?.name === "ChunkLoadError" ||
  /loading chunk|loading css chunk|failed to fetch dynamically imported module/i.test(error?.message || "");

const COUNTDOWN_SECONDS = 5;

const ErrorComponent = ({ error, resetErrorBoundary }) => {
  const isChunk = isChunkLoadError(error);
  const [countdown, setCountdown] = useState(isChunk ? COUNTDOWN_SECONDS : null);

  useEffect(() => {
    if (!isChunk) return;
    if (countdown === 0) {
      window.location.reload();
      return;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [isChunk, countdown]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
      }}
    >
      <Typography variant="h1" component="h2" sx={{ fontSize: "5rem", fontWeight: "bold", color: "error.main" }}>
        Oops!
      </Typography>

      {isChunk ? (
        <>
          <Typography variant="h5" sx={{ marginY: 2, textAlign: "center" }}>
            Connection issue detected.
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: 3, textAlign: "center" }}>
            A page resource couldn&apos;t load — this is usually caused by a brief internet interruption.
            {countdown !== null && countdown > 0 && (
              <>
                {" "}
                Retrying automatically in <strong>{countdown}</strong>s&hellip;
              </>
            )}
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {countdown !== null && countdown > 0 && <CircularProgress size={20} />}
            <Button variant="contained" color="primary" onClick={() => window.location.reload()}>
              Retry Now
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Typography variant="h5" sx={{ marginY: 2, textAlign: "center" }}>
            Something went wrong.
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: 3, textAlign: "center" }}>
            We encountered an unexpected error. Please try again later.
          </Typography>
          <Button variant="contained" color="primary" onClick={() => resetErrorBoundary()}>
            Reload Page
          </Button>
        </>
      )}
    </Box>
  );
};

ErrorComponent.propTypes = {
  error: PropTypes.object,
  resetErrorBoundary: PropTypes.func,
};

export default ErrorComponent;
